import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { colors } from "@zapalacorp/tokens";
import { format } from "date-fns";

import History, { Item } from "Components/Generic/History";
import { Color } from "Components/Form/Button";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../../../components/Generic/ViewJobComponent";
import { SkeletonDetails } from "../../../../../../components/Shipments/Maintenance/Details";
import { VoyageDetails } from "Components/Tracking";
import { ContainerPosition, ContainerStatus } from "../../../../../../models/marine.traffic";
import MarineTrafficService from "../../../../../../services/marine.traffic.service";
import WaybillsService from "../../../../../../services/waybill.service";
import { ShipmentStatus, ShipmentType } from "../../../../../../util/enums";
import { WaybillsDetails } from "../../Maintenance/components/Details";
import Orders from "../../Maintenance/components/Orders";
import { useShipmentContext } from "../../Maintenance/hooks/useContext";
import { useAppState } from "Context/StateContext";
import useStyles from "./styles";
import { capitalizeFirstLetter } from "Util/capitalizeFirstLetter";

import useActionShipment from "Hooks/useActionShipment";
import { ShipmentHistory } from "Models/history";
import { FiActivity } from "react-icons/fi";

interface WaybillViewProps {}

export interface WaybillViewHandle {
  onOpenView: (id: number) => void;
}

const WaybillView: ForwardRefRenderFunction<WaybillViewHandle, WaybillViewProps> = (props, ref) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const sideViewComponentRef = useRef<PropHandles>(null);

  const [waybillId, setWaybillId] = useState<number>();

  const { shipment: waybill, setShipment } = useShipmentContext();

  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();
  const [containerPosition, setContainerPosition] = useState<ContainerPosition>();

  const classes = useStyles();

  const shipmentTrackingKey = useMemo(() => {
    if (!waybill) return undefined;

    if (waybill.shipmentType === ShipmentType.SEA) {
      const seaFields = [waybill.billOfLanding, waybill.bookingNo, waybill.containerNumbers];
      return seaFields.find((field) => !!field);
    }

    if (waybill.shipmentType === ShipmentType.AIR) {
      return waybill.airwayBill;
    }

    return undefined;
  }, [waybill]);

  const setViewTitle = useMemo(() => {
    return `${translate("shipment")} #${waybill?.containerId ?? ""}`;
  }, [translate, waybill?.containerId]);

  const onOpenView = (id: number) => {
    setWaybillId(id);
    sideViewComponentRef.current?.setOpenSideBar(true);
  };

  useImperativeHandle(ref, () => ({
    onOpenView,
  }));

  const { getShipmentHistories } = useActionShipment();

  const { data: histories = [], isFetching: isFetchingHistories } = getShipmentHistories(waybillId);

  const historyItems = useMemo<Item[]>(() => {
    const firstHistoryIp = (
      histories.find((history: ShipmentHistory) => history?.userIpAddress)?.userIpAddress ?? ""
    ).trim();

    if (!histories.length) {
      return [
        {
          title: "Shipment created",
          activityDate: `${format(new Date(), "yyyy/MM/dd hh:mmbb")}`,
          activityUser: `${currentUser?.name} ${currentUser?.lastName}`,
          activityUserIp: firstHistoryIp,
        } as Item,
      ];
    }

    return histories.map((history: ShipmentHistory) => {
      const user = history?.user ? `${history.user.name} ${history.user.lastName ?? ""}` : "";

      return {
        title: capitalizeFirstLetter(history?.action ?? ""),
        activityDate: history?.updatedAt
          ? `${format(new Date(history.updatedAt), "yyyy/MM/dd hh:mmbb")}`
          : "",
        activityUser: user,
        activityUserIp: history.userIpAddress ?? "",
      } as Item;
    });
  }, [histories, currentUser]);

  const buttonsComponent = useMemo(() => {
    return [
      {
        buttonLabel: translate("seeActivity"),
        titlePage: translate("activity"),
        color: Color.SECONDARY,
        icon: <FiActivity size={16} color={colors.brandBlue500} />,
        renderComponent: () => <History items={historyItems} />,
      },
    ];
  }, [translate, historyItems]);

  const { isFetching } = useQuery(
    ["waybills.list", waybillId],
    () =>
      WaybillsService.getById(Number(waybillId), {
        include: JSON.stringify([
          {
            association: "jobs",
            include: [
              { association: "itemsJob" },
              { association: "customer" },
              { association: "origins" },
              { association: "destinations" },
            ],
          },
          { association: "containers" },
          { association: "milestones", include: [{ association: "milestone", required: true }] },
          { association: "shipmentTracking", include: [{ association: "vesselPosition" }] },
          {
            association: "shipmentAgents",
            include: [{ association: "agent" }],
          },
          { association: "documents" },
          {
            association: "customersToShipment",
            include: [{ association: "customer" }],
          },
        ]),
      }),
    {
      enabled: !!waybillId,
      onSuccess: (response) => {
        if (response && response?.data) {
          setShipment(response.data);
        }
      },
    }
  );

  const { isFetching: isFetchingContainerStatus } = useQuery(
    [`container.status.${waybill?.id}`],
    () =>
      MarineTrafficService.viewShipmentTracking(
        shipmentTrackingKey ?? "",
        waybill?.id?.toString() ?? ""
      ),
    {
      enabled:
        !!shipmentTrackingKey &&
        waybill?.shipmentTracking?.shipmentStatus?.toLowerCase() !==
          ShipmentStatus.WAITING_FOR_UPDATE.toLowerCase(),
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: false,
      onSuccess: (response) => {
        if (!response.data) {
          setContainerStatus(undefined);
          setContainerPosition(undefined);
          return;
        }

        const position = {
          TIMESTAMP: waybill?.shipmentTracking?.updatedAt,
          LAT: waybill?.shipmentTracking?.vesselPosition?.latitude ?? "",
          LON: waybill?.shipmentTracking?.vesselPosition?.longitude ?? "",
        } as unknown as ContainerPosition;

        setContainerStatus(response.data);
        setContainerPosition(position);
      },
    }
  );

  const navigation = useMemo(
    () => [{ key: Navigation.DETAILS }, { key: Navigation.ORDERS }, { key: Navigation.TRACKING }],
    []
  );

  const tabs = useMemo(
    () => [
      {
        key: Navigation.DETAILS,
        loading: isFetching,
        isEmpty: !waybill,
        renderLoading: () => <SkeletonDetails />,
        render: () => <WaybillsDetails waybillView={waybill} onViewMode />,
      },
      {
        key: Navigation.ORDERS,
        loading: isFetching,
        isEmpty: !waybill,
        renderLoading: () => <SkeletonDetails />,
        render: () => (
          <Orders
            orders={waybill?.jobs ?? []}
            customers={waybill?.customersToShipment ?? []}
            onViewMode
          />
        ),
      },
      {
        key: Navigation.TRACKING,
        loading: isFetching || isFetchingContainerStatus,
        isEmpty: !containerStatus,
        renderLoading: () => <SkeletonDetails />,
        render: () => (
          <VoyageDetails
            shipment={waybill}
            containerPosition={containerPosition}
            containerStatus={containerStatus}
            isOnViewComponent
            viewLivePosition={process.env.NODE_DATABASE !== "work"}
          />
        ),
      },
    ],
    [
      waybill,
      containerPosition,
      containerStatus,
      isFetching,
      isFetchingContainerStatus,
      containerStatus,
    ]
  );

  return (
    <SideViewComponent
      ref={sideViewComponentRef}
      title={setViewTitle}
      isLoading={isFetching || isFetchingContainerStatus || isFetchingHistories}
      tabs={tabs}
      navigation={navigation}
      buttonsComponent={buttonsComponent}
    />
  );
};

export default forwardRef(WaybillView);
